import React, { useRef, useEffect, KeyboardEvent, useState, useImperativeHandle, useCallback } from 'react';
import Inputmask from 'inputmask';
import './styles.scss';
import { Label } from '../Label/index';
import _uniqueId from 'lodash/uniqueId';
import { Underline } from '../Underline';
import TsrSvgIcon from '../../assets/icons';
import { InputContainer } from '../InputContainer';
import { InputPropsBase } from '../interfaces/Inputs';
import TsrTooltip from '../../tooltip';
import { TooltipType } from '../models/TooltipType';
import { debounce, round } from 'lodash';
import { Variant } from '../models';
import { KEY_ARROW_UP, KEY_ARROW_DOWN } from '../models/EventKeysConsts';

interface CurrencyInputProps extends InputPropsBase<number> {
    step?: number;
    digits?: number;
    min?: number;
    max?: number;
}

export const CurrencyInput = React.forwardRef<HTMLInputElement, React.PropsWithChildren<CurrencyInputProps>>((props, ref) => {
    const step = props.step || 1;
    const digits = props.digits || 2;
    const min = props.min || 0;
    const max = props.max || 99999999.99;
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current!);
    const id = _uniqueId('id-currency-input-');
    const initialValueZeros = '0'.repeat(digits);
    const [isMouseDown, setIsMouseDown] = useState(false);
    const [text, setText] = useState<string | number>(props.value || props.min || `0.${initialValueZeros}`);
    const onChangeSetTextHandler = (value: string) => {
        setText(value);
    };
    const [onFocus, setOnFocus] = useState(false);
    const timeoutTime = 300;
    const intervalTime = 50;

    const onBlur = () => {
        setOnFocus(false);
        document.removeEventListener('wheel', preventScroll, true);

        const num = Number(text);

        if (isNaN(num) || num < min) {
            props.onChange?.(min);
            props.onBlur?.(min);
            setText(min);
        } else if (max !== undefined && num > max) {
            props.onChange?.(max);
            props.onBlur?.(max);
            setText(max);
        } else {
            props.onChange?.(num);
            props.onBlur?.(num);
            setText(num);
        }
    };

    useEffect(() => {
        setText(props.value);
    }, [props.value]);

    const timeout = useRef<ReturnType<typeof setTimeout>>();
    const interval = useRef<ReturnType<typeof setInterval>>();
    useEffect(() => {
        if (!inputRef.current) {
            return;
        }
        Inputmask({
            alias: 'currency',
            groupSeparator: '',
            SetMaxOnOverflow: true,
            max: max,
            min: min,
            digits: digits,
            rightAlign: false,
            positionCaretOnClick: 'none',
            oncleared() {
                if (
                    inputRef.current?.selectionStart === (text as string).length &&
                    inputRef.current?.selectionEnd === (text as string).length
                ) {
                    setText(`0.${initialValueZeros}`);
                    inputRef.current?.setSelectionRange(1, 1);
                }
            },
        }).mask(inputRef.current);
    }, [digits, initialValueZeros, max, min]);

    const debouncedOnChange = useCallback(
        debounce((value: string | number) => props.onChange && props.onChange(value as number), 275),
        [],
    );
    const incrementValueHandler = (change: number): void => {
        if (!inputRef.current || !inputRef.current.value || inputRef.current.selectionStart === null) {
            return;
        }
        const selectionStart = inputRef.current.selectionStart,
            selectionEnd = inputRef.current.selectionEnd,
            selectionEndInput = selectionEnd === inputRef.current.value.length,
            selectionEndInteger = selectionEnd === inputRef.current.value.length - digits - 1;

        let selectionStartNew = inputRef.current.selectionStart;
        let updatedSelectionStart = 0;
        selectionStartNew == null ? (selectionStartNew = updatedSelectionStart) : (updatedSelectionStart = selectionStartNew);
        let carretPosition = selectionStartNew - inputRef.current.value.length;
        if (-carretPosition === String(text).length && change < 0) {
            if (+inputRef.current.value[0] === 1) {
                carretPosition += 2;
            }
            if (+inputRef.current.value[0] > 1) {
                carretPosition += 1;
            }
        }
        let updatedCarretPosition =
            carretPosition < 0 - digits
                ? Math.pow(10, Math.abs(carretPosition + 1) - digits)
                : step / Math.pow(10, digits + carretPosition);

        if (change < 0) {
            updatedCarretPosition = updatedCarretPosition * -1;
        }
        const newValue = +inputRef.current.value + updatedCarretPosition;
        inputRef.current.value = String(round(newValue, digits));
        inputRef.current.selectionEnd = selectionEndInput
            ? inputRef.current.value.length
            : selectionEndInteger
            ? inputRef.current.value.length - digits - 1
            : selectionEnd;
        inputRef.current.selectionStart = selectionStart === selectionEnd ? inputRef.current.selectionEnd : selectionStart;
        setText(inputRef.current.value);
        (props.variant === Variant.AgGrid || props.immediatelyChange) && debouncedOnChange(inputRef.current.value);
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChangeSetTextHandler(e.target.value);
    };

    const onMouseDownHandler = useCallback((change: number, e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        e.preventDefault();
        inputRef.current?.focus();
        incrementValueHandler(change);
        setIsMouseDown(true);
        timeout.current = setTimeout(() => {
            interval.current = setInterval(() => {
                incrementValueHandler(change);
                setIsMouseDown(true);
            }, intervalTime);
        }, timeoutTime);
    }, []);

    const clearTimeOuts = useCallback((): void => {
        if (interval.current) {
            clearTimeout(interval.current);
        }
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        if (isMouseDown) {
            (props.variant === Variant.AgGrid || props.immediatelyChange) && debouncedOnChange(text);
            document.removeEventListener('mouseup', clearTimeOuts);
            setIsMouseDown(false);
        }
    }, [text]);

    const onMouseUp = useCallback((): void => {
        if (interval.current) {
            clearTimeout(interval.current);
        }
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        setIsMouseDown(false);
    }, [text]);

    useEffect(() => {
        if (isMouseDown) {
            document.addEventListener('mouseup', clearTimeOuts);
        }
    }, [isMouseDown, text]);

    const onKeyDownHandler = (event: KeyboardEvent): void => {
        if (event.key === KEY_ARROW_UP) {
            event.preventDefault();
            incrementValueHandler(step);
        }
        if (event.key === KEY_ARROW_DOWN) {
            event.preventDefault();
            incrementValueHandler(-step);
        }
    };

    const onKeyUpHandler = (e: any): void => {
        (props.variant === Variant.AgGrid || props.immediatelyChange) && props.onChange?.(text as number);
    };

    const preventScroll = useCallback((e: globalThis.WheelEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.deltaY < 0 ? incrementValueHandler(step) : incrementValueHandler(-step);
        return false;
    }, []);

    useEffect(() => {
        if (onFocus) {
            document.addEventListener('wheel', preventScroll, { passive: false, capture: true });
        } else {
            document.removeEventListener('wheel', preventScroll, true);
        }

        return () => document.removeEventListener('wheel', preventScroll, true);
    }, [onFocus, preventScroll]);

    return (
        <InputContainer
            gap={props.gap}
            justify={props.justify}
            align={props.align}
            width={props.width || '100%'}
            className={`${props.variant}`}
        >
            <Label
                id={props.id || id}
                content={props.label}
                disabled={props.disabled}
                labelPosition={props.labelPosition}
                labelLength={props.labelLength || 1}
                overflow={props.overflow}
            >
                <div
                    className={
                        'tsr-input-currency' +
                        (props.disabled === true ? ' disabled' : '') +
                        (props.className ? ' ' + props.className : '')
                    }
                    style={{ flex: props.inputLength || 1, width: props.inputFieldWidth }}
                >
                    <input
                        type="text"
                        id={id}
                        className={'input-type' + (props.disabled === true ? ' disabled' : '')}
                        ref={inputRef}
                        value={text}
                        onKeyDown={onKeyDownHandler}
                        onKeyUp={onKeyUpHandler}
                        onChange={onChange}
                        onBlur={onBlur}
                        onFocus={() => setOnFocus(true)}
                        disabled={props.disabled === 'inputOnly' || props.disabled === true}
                    ></input>
                    {props.errorMessage && !props.disabled ? (
                        <TsrTooltip type={TooltipType.Error} icon={true} content={props.errorMessage}>
                            <TsrSvgIcon icon="information" color="error" pointer />
                        </TsrTooltip>
                    ) : props.infoMessage && !props.disabled ? (
                        <TsrTooltip icon={true} content={props.infoMessage}>
                            <TsrSvgIcon icon="information" color="info" pointer />
                        </TsrTooltip>
                    ) : (
                        props.infoMessage && <TsrSvgIcon icon="information" color="disabled" />
                    )}
                    <div className="buttons">
                        <button
                            type="button"
                            className={'mouse' + (props.disabled === true ? ' disabled' : '')}
                            tabIndex={-1}
                            disabled={props.disabled === 'inputOnly' || props.disabled === true}
                            onMouseDown={e => onMouseDownHandler(step, e)}
                            onMouseUp={onMouseUp}
                        >
                            <TsrSvgIcon icon="arrowUp" color="info" pointer />
                        </button>
                        <button
                            type="button"
                            className={'mouse' + (props.disabled === true ? ' disabled' : '')}
                            tabIndex={-1}
                            disabled={props.disabled === 'inputOnly' || props.disabled === true}
                            onMouseDown={e => onMouseDownHandler(-step, e)}
                            onMouseUp={onMouseUp}
                        >
                            <TsrSvgIcon icon="arrowDown" color="info" pointer />
                        </button>
                    </div>

                    <Underline disabled={props.disabled === 'inputOnly' || props.disabled === true} />
                </div>
            </Label>
        </InputContainer>
    );
});
